//------------------------------------------------------------------------
// Comparison Vertical Bar Chart
//------------------------------------------------------------------------
// NOTE: Only import the necessary modules
// https://www.chartjs.org/docs/latest/getting-started/integration.html
// NOTE: Treeshaking doesn’t seem to be working when importing everything
// https://github.com/chartjs/Chart.js/issues/10163
// e.g. import Chart from 'chart.js/auto';
import {Chart, BarElement, BarController, Tooltip, LinearScale, CategoryScale} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { axis_font_opts, cursorPositioner } from '../helpers/chart-js-helpers';
import debounce from "lodash/debounce";

Chart.register(BarElement, BarController, Tooltip, LinearScale, CategoryScale);

/**
 * Custom positioner - Allows for the tooltip to follow the cursor's pointer
 */
Tooltip.positioners.cursor = cursorPositioner;

class ComparisonVerticalBarChart {
  constructor(config) {
    this.el = config.el;
    this.canvas = this.el.querySelector('canvas');
    this.legendItems = this.el.querySelectorAll('[data-legend-item]');

    if (!this.canvas) {
      console.error(`Unable to initialize vertical bar chart, no matching element for ${this.el}.`);
      return false;
    }

    this.setData();
    this.setChartConfig();
    this.init();
  }

  init() {
    this.chart = new Chart(this.canvas, {
      type: 'bar',
      data: this.data,
      plugins: this.plugins,
      options: this.chartOpts
    });

    this.el.addEventListener('mouseover-chart', debounce(this.mouseoverChartHandler.bind(this), 100));

    this.el.addEventListener('mouseout-chart', debounce(this.mouseoutChartHandler.bind(this), 100));
  }

  mouseoverChartHandler(evt) {
    let { dataPoints } =  evt.detail.tooltip;
    // let { chart } = evt.detail;

    if (dataPoints.length < 1) {
      return false;
    }

    let selectedIndex = dataPoints[0].datasetIndex;

    this.legendItems.forEach(item => {
      let legendIndex = item.getAttribute('data-index');

      if (parseInt(legendIndex, 10) === parseInt(selectedIndex, 10)) {
        item.classList.remove('is-inactive');
      } else {
        item.classList.add('is-inactive');
      }
    });
  }

  mouseoutChartHandler() {
    this.legendItems.forEach(item => item.classList.remove('is-inactive'));
  }

  externalTooltipHandler(context) {
    const { chart, tooltip } = context;
    const tooltipEl = chart.canvas.nextElementSibling;

    // Custom event detail that is being passed through
    let detail = {
      detail: {
        el: this.el,
        tooltip,
        chart
      }
    };

    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = 0;
      const mouseoutChart = new CustomEvent("mouseout-chart", detail);

      this.el.dispatchEvent(mouseoutChart);
      return;
    }

    // Set Text
    if (tooltip.dataPoints) {
      // const { dataIndex } = tooltip;
      const tooltipFragment = document.createDocumentFragment();
      let dataPoints = tooltip.dataPoints || [];

      dataPoints.forEach(item => {

        // get the total sum of the bar
        let totalSum = item.dataset.data.reduce((previousValue, currentValue) => parseInt(previousValue, 10) + parseInt(currentValue, 10), 0);

        // The total sum elements and text node
        let totalValueEl = document.createElement('span');
        let totalValueText = document.createTextNode(totalSum);

        // The bar labels
        let titleEl = document.createElement('span');
        let titleText = document.createTextNode(item.label);

        // The segment label
        let segmentLabelEl = document.createElement('span');
        let segmentLabelText = document.createTextNode(item.dataset.label);

        // The segment value
        let segmentValueEl = document.createElement('span');
        let segmentValueText = document.createTextNode(item.formattedValue);

        // The segment "out of" value, e.g. 8 / 22 - the " / 22" is the value
        let segmentBaseValueEl = document.createElement('span');
        let segmentBaseValueText = document.createTextNode('/' + totalSum);

        titleEl.classList.add('ChartTooltip-title');
        totalValueEl.classList.add('ChartTooltip-value');
        segmentLabelEl.classList.add('ChartTooltip-title');
        segmentValueEl.classList.add('ChartTooltip-value');
        segmentBaseValueEl.classList.add('ChartTooltip-base');

        titleEl.appendChild(titleText);
        totalValueEl.appendChild(totalValueText);
        segmentLabelEl.appendChild(segmentLabelText);
        segmentValueEl.appendChild(segmentValueText);
        segmentBaseValueEl.appendChild(segmentBaseValueText);

        segmentValueEl.appendChild(segmentBaseValueEl);
        tooltipFragment.appendChild(segmentLabelEl);
        tooltipFragment.appendChild(segmentValueEl);
        tooltipFragment.appendChild(titleEl);
        tooltipFragment.appendChild(totalValueEl);
      });

      while(tooltipEl.firstChild) {
        tooltipEl.firstChild.remove();
      }

      tooltipEl.appendChild(tooltipFragment);
    }

    const { offsetLeft, offsetTop } = chart.canvas;
    tooltipEl.style.opacity = 1;
    tooltipEl.style.left = offsetLeft + (tooltip.caretX + 10) + 'px';
    tooltipEl.style.top = offsetTop + (tooltip.caretY + 10) + 'px';

    // Emit event when we are hovering over the segment
    const mouseoverChart = new CustomEvent("mouseover-chart", detail);
    this.el.dispatchEvent(mouseoverChart);
  }

  /**
   * Retrieves data-* attributes from the Chart's container and create instance variables
   * of each in the correct format for Chart.js
   *
   * These are the data-* attributes that are being used:
   * - data-segments = the segments from the block's segments repeater field
   * - data-title = the chart's title
   * - data-type = the type of circle chart that is being used
   *
   * isntance variables that are being created:
   * - this.datasets
   * - this.dataValues
   * @return null
   */
  setData() {
    this.userDefinedOpts = JSON.parse(this.el.getAttribute('data-chart-opts'));
    this.datasets = JSON.parse(this.el.getAttribute('data-datasets'));
    this.dataValues = [];

    // Add all of the values in the datasets into this.dataValues
    // this is used to calcuate values for stepSize() and suggestedMax()
    this.datasets.forEach(set => {
      set.data.forEach(num => {
        this.dataValues.push(num);
      });
    });

    this.datasets.forEach((data, index, arr) => {
      let color = data.backgroundColor + 'e0';

      arr[index].backgroundColor = color;
      arr[index].borderColor = color;
    });

    this.dataLabels = JSON.parse(this.el.getAttribute('data-labels'));

    this.data = {
      labels: this.dataLabels,
      datasets: this.datasets
    };
  }

  /**
   * Sets the values for the options and plugins properties in Chart.JS config
   */
  setChartConfig() {
    let mql = window.matchMedia("(min-width: 700px)");

    mql.addEventListener('change', () => {
      // Returning the same values that onResize would for chart.js
      let width  = window.innerWidth;
      let tooltip = (width > 700) ? this.externalTooltipHandler.bind(this) : false;

      this.chart.options.plugins.tooltip.external = tooltip;
      this.chart.update();
    });

    let datalabelsFontSize = (window.innerWidth > 700) ? 22 : 18;
    let barLabelFontSize = (window.innerWidth > 700) ? 18 : 16;
    let tickMarkFontSize = (window.innerWidth > 700) ? 16 : 14;
    let chartFontColor = "#404040"; // $c-default / fs-color("text", "gray-dark");

    this.chartOpts = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: this.userDefinedOpts.y_axis_title ? true : false, // displays y axis title via user specified value
            text: this.userDefinedOpts.y_axis_title, // displays y axis title via user specified value
            font: axis_font_opts(),
            color: chartFontColor
          },
          ticks: {
            display: this.userDefinedOpts.show_y_axis, // displays y axis ticks, e.g. the step counts
            font: {
              size: tickMarkFontSize,
              family: "'Luma Sans', 'Helvetica', 'Arial', sans-serif",
              weight: 'normal'
            },
            color: chartFontColor,
            stepSize: () => {
              let max = this.dataValues.reduce((accumulator, currentValue) => {
                return Math.max(accumulator, currentValue)
              });

              // We want a maximum of six steps per graph
              let step = Math.ceil(max / 6);

              // We are rounding it up to the nearest tenth to prevent odd numbers
              // https://stackoverflow.com/a/11022517
              return Math.ceil(step / 10) * 10;
            },
          },
          grid: {
            display: this.userDefinedOpts.show_gridlines ? this.userDefinedOpts.show_gridlines : false// shows or hides the horizontal gridlines on the chart
          },
          suggestedMax: () => {
            // The maximum number for the Y axis value. This is done dynamically
            // so we can have space for the data labels to appear of the bar without worrying about collision
            let max = this.dataValues.reduce((accumulator, currentValue) => {
              return Math.max(accumulator, currentValue)
            });

            return max * 1.15; // return 110% of the maximum value
          }
        },
        x: {
          title: {
            display: this.userDefinedOpts.x_axis_title ? true : false,
            text: this.userDefinedOpts.x_axis_title,
            font: axis_font_opts(),
            color: chartFontColor
          },
          ticks: {
            display: true, // setting false hides the values on the x axis
            color: chartFontColor,
            font: {
              size: barLabelFontSize,
              family: "'Luma Sans', 'Helvetica', 'Arial', sans-serif",
              weight: 'normal'
            },
          },
          grid: {
            display: false
          },
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          enabled: false,
          position: 'cursor',
          external: mql.matches ? this.externalTooltipHandler.bind(this) : false,
        }
      },
      onResize: debounce(this.resizeHandler.bind(this), 10)
    };

    // Add Chart.js plugins here
    this.plugins = [];

    // Add data labels if user wants it to appear by checking the show_data_series_count parameter
    if (this.userDefinedOpts.show_series_count) {
      this.plugins.push(ChartDataLabels);
      this.chartOpts.plugins.datalabels = {
        font: {
          family: "'Luma Sans', 'Helvetica', 'Arial', sans-serif",
          size: datalabelsFontSize,
          lineHeight: 1.3,
          weight: 'bold',
        },
        color: '#480751', // fs-color("text", "purple")
        anchor: 'end',
        clamp: true, // only put the data label inside of the chart
        align: 'top'
      }
    }
  }

  resizeHandler(chart, size) {
    let { width } = size;

    let datalabelsFontSize = (width > 700) ? 22 : 18;
    let tickMarkFontSize = (width > 700) ? 16 : 14;

    let datalabelsFont = {
      size: datalabelsFontSize,
      family: "'Luma Sans', 'Helvetica', 'Arial', sans-serif",
      weight: 'bold'
    };

    let ticksFont = {
      size: tickMarkFontSize,
      family: "'Luma Sans', 'Helvetica', 'Arial', sans-serif",
      weight: 'normal'
    }

    chart.options.scales.y.ticks = {
      display: (this.userDefinedOpts.show_y_axis && width > 700) ? this.userDefinedOpts.show_y_axis : false,
      font: ticksFont
    };

    chart.options.plugins.datalabels.font = datalabelsFont;
    chart.options.scales.x.ticks.font = ticksFont;

    chart.update();
  }
}

const init = () => {
  const comparisonbarCharts = document.querySelectorAll('[data-comparison-bar-chart]');

  if (comparisonbarCharts) {
    comparisonbarCharts.forEach(chart => {
      new ComparisonVerticalBarChart({el: chart});
    })
  }
}

// Init on initial page load
init();
